import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellDot } from "lucide-react";
import "animate.css";

const NotificationBell = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [notifications, setNotifications] = useState([
    { id: 1, message: "Order #123 confirmed!", route: "/orders/123" },
    { id: 2, message: "New message from Admin", route: "/messages" },
    { id: 3, message: "Your product was approved", route: "/products" },
  ]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Prevent body scroll when dropdown is open on mobile
  useEffect(() => {
    if (isMobile && showDropdown) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, showDropdown]);

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Bell clicked, current state:", showDropdown);
    setShowDropdown((prev) => {
      const newState = !prev;
      console.log("Setting dropdown to:", newState);
      return newState;
    });
  };

  const handleNotificationClick = (id, route) => {
    navigate(route);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    setShowDropdown(false);
  };

  const handleMarkAllRead = () => {
    setNotifications([]);
    setShowDropdown(false);
  };

  return (
    <>
      <div 
        className="position-relative d-flex align-items-center" 
        ref={dropdownRef}
        style={{ 
          marginRight: "15px",
          zIndex: 1050
        }}
      >
        {/* Bell Icon Container */}
        <div
          className="d-flex align-items-center justify-content-center position-relative"
          onClick={toggleDropdown}
          style={{ 
            cursor: "pointer",
            padding: "8px",
            borderRadius: "50%",
            transition: "background-color 0.2s ease",
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(34, 190, 13, 0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          {notifications.length > 0 ? (
            <BellDot 
              size={24} 
              style={{ color: "rgb(34, 190, 13)" }} 
            />
          ) : (
            <Bell 
              size={24} 
              style={{ color: "rgb(34, 190, 13)" }} 
            />
          )}

          {/* Badge */}
          {notifications.length > 0 && (
            <span
              className="position-absolute badge rounded-pill bg-danger"
              style={{ 
                fontSize: "10px",
                top: "5px",
                right: "5px",
                minWidth: "16px",
                height: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none"
              }}
            >
              {notifications.length > 99 ? "99+" : notifications.length}
            </span>
          )}
        </div>

        {/* Desktop Dropdown */}
        {!isMobile && (
          <div
            className={`position-absolute bg-white border rounded shadow-lg ${
              showDropdown ? 'animate__animated animate__fadeInDown' : ''
            }`}
            style={{
              top: "calc(100% + 8px)",
              right: 0,
              width: "320px",
              maxHeight: "450px",
              overflowY: "auto",
              zIndex: 10000,
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
              display: showDropdown ? "block" : "none",
              transform: "translateZ(0)",
              backfaceVisibility: "hidden"
            }}
          >
            {notifications.length > 0 ? (
              <>
                {/* Header */}
                <div 
                  className="d-flex justify-content-between align-items-center px-3 py-3 border-bottom"
                  style={{ backgroundColor: "#f8f9fa" }}
                >
                  <h6 className="mb-0 fw-semibold" style={{ fontSize: "15px", color: "#495057" }}>
                    Notifications ({notifications.length})
                  </h6>
                  <button
                    className="btn btn-sm text-danger p-0"
                    onClick={handleMarkAllRead}
                    style={{ 
                      fontSize: "13px", 
                      textDecoration: "none",
                      background: "none",
                      border: "none",
                      fontWeight: "500"
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                    onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                  >
                    Clear all
                  </button>
                </div>
                
                {/* Notifications List */}
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {notifications.map((n, index) => (
                    <div
                      key={n.id}
                      onClick={() => handleNotificationClick(n.id, n.route)}
                      className="d-flex align-items-start p-3"
                      style={{
                        cursor: "pointer",
                        borderBottom: index < notifications.length - 1 ? "1px solid #f1f3f4" : "none",
                        transition: "background-color 0.2s ease"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f8f9fa";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <div className="flex-shrink-0 me-3">
                        <div 
                          className="bg-primary rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: "8px", height: "8px", backgroundColor: "rgb(34, 190, 13)" }}
                        ></div>
                      </div>
                      <div className="flex-grow-1">
                        <p className="mb-0 text-dark" style={{ 
                          fontSize: "14px", 
                          lineHeight: "1.4",
                          fontWeight: "400"
                        }}>
                          {n.message}
                        </p>
                        <small className="text-muted" style={{ fontSize: "12px" }}>
                          Just now
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-5">
                <Bell size={48} className="mb-3 text-muted opacity-50" />
                <p className="mb-0 text-muted" style={{ fontSize: "14px" }}>
                  No new notifications
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Modal Overlay */}
      {isMobile && showDropdown && (
        <div
          className={`position-fixed w-100 h-100 ${
            showDropdown ? 'animate__animated animate__fadeIn' : ''
          }`}
          style={{
            top: 0,
            left: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 10050,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            paddingTop: "60px"
          }}
          onClick={() => setShowDropdown(false)}
        >
          {/* Mobile Modal Content */}
          <div
            className={`bg-white border rounded shadow-lg mx-3 ${
              showDropdown ? 'animate__animated animate__slideInDown' : ''
            }`}
            style={{
              width: "calc(100vw - 24px)",
              maxWidth: "400px",
              maxHeight: "calc(100vh - 120px)",
              overflowY: "auto",
              border: "1px solid #dee2e6",
              borderRadius: "12px",
              boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {notifications.length > 0 ? (
              <>
                {/* Mobile Header */}
                <div 
                  className="d-flex justify-content-between align-items-center px-4 py-4 border-bottom"
                  style={{ backgroundColor: "#f8f9fa" }}
                >
                  <h6 className="mb-0 fw-semibold" style={{ fontSize: "16px", color: "#495057" }}>
                    Notifications ({notifications.length})
                  </h6>
                  <button
                    className="btn btn-sm text-danger p-0"
                    onClick={handleMarkAllRead}
                    style={{ 
                      fontSize: "14px", 
                      textDecoration: "none",
                      background: "none",
                      border: "none",
                      fontWeight: "500"
                    }}
                  >
                    Clear all
                  </button>
                </div>
                
                {/* Mobile Notifications List */}
                <div style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
                  {notifications.map((n, index) => (
                    <div
                      key={n.id}
                      onClick={() => handleNotificationClick(n.id, n.route)}
                      className="d-flex align-items-start p-4"
                      style={{
                        cursor: "pointer",
                        borderBottom: index < notifications.length - 1 ? "1px solid #f1f3f4" : "none",
                        transition: "background-color 0.2s ease",
                        minHeight: "60px"
                      }}
                      onTouchStart={(e) => {
                        e.currentTarget.style.backgroundColor = "#f8f9fa";
                      }}
                      onTouchEnd={(e) => {
                        setTimeout(() => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }, 150);
                      }}
                    >
                      <div className="flex-shrink-0 me-3" style={{ marginTop: "4px" }}>
                        <div 
                          className="bg-primary rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: "10px", height: "10px", backgroundColor: "rgb(34, 190, 13)" }}
                        ></div>
                      </div>
                      <div className="flex-grow-1">
                        <p className="mb-1 text-dark" style={{ 
                          fontSize: "15px", 
                          lineHeight: "1.4",
                          fontWeight: "400"
                        }}>
                          {n.message}
                        </p>
                        <small className="text-muted" style={{ fontSize: "13px" }}>
                          Just now
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-5">
                <Bell size={56} className="mb-3 text-muted opacity-50" />
                <p className="mb-0 text-muted" style={{ fontSize: "15px" }}>
                  No new notifications
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationBell;
